import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import ProfileCard from "../../components/ProfileCard/ProfileCard";
import Galaxy from "../../components/Galaxy/Galaxy";

import "./AboutUs.css";


import TeamYostian from "../../assets/team-yostian.webp";
import TeamDavid from "../../assets/team-david.webp";
import TeamSnayder from "../../assets/team-snayder.jpg";
import TeamMarlon from "../../assets/team-marlon.webp";
import TeamMarlonM from "../../assets/team-MarlonM.webp";

const BASE_GALAXY_SPEED = 0.95;
const CLICK_GALAXY_SPEED = 2.4;
const CLICK_GALAXY_BOOST_MS = 650;
const BASE_GALAXY_GLOW = 0.14;
const CLICK_GALAXY_GLOW = 0.23;

export default function Aboutus() {
  const [selectedCard, setSelectedCard] = useState(null);
  const { t } = useTranslation();

  const roadmapQuarters = t("aboutUs.roadmap.quarters", { returnObjects: true });
  const quarterKeys = Object.keys(roadmapQuarters || {});
  const [activeQuarterKey, setActiveQuarterKey] = useState(null);
  const [galaxySpeed, setGalaxySpeed] = useState(BASE_GALAXY_SPEED);
  const [galaxyGlow, setGalaxyGlow] = useState(BASE_GALAXY_GLOW);
  const galaxyBoostTimerRef = useRef(null);

  const activeQuarterLabel = activeQuarterKey ? t(`aboutUs.roadmap.quarters.${activeQuarterKey}`) : "";
  const activeQuarterContent = activeQuarterKey
    ? t(`aboutUs.roadmap.modalContent.${activeQuarterKey}`, { returnObjects: true }) || []
    : [];

  const handleQuarterSelect = (quarterKey) => {
    if (!quarterKey) {
      return;
    }

    if (galaxyBoostTimerRef.current) {
      clearTimeout(galaxyBoostTimerRef.current);
    }

    setGalaxySpeed(CLICK_GALAXY_SPEED);
  setGalaxyGlow(CLICK_GALAXY_GLOW);
    setActiveQuarterKey(quarterKey);

    galaxyBoostTimerRef.current = setTimeout(() => {
      setGalaxySpeed(BASE_GALAXY_SPEED);
      setGalaxyGlow(BASE_GALAXY_GLOW);
      galaxyBoostTimerRef.current = null;
    }, CLICK_GALAXY_BOOST_MS);
  };

  useEffect(() => {
    return () => {
      if (galaxyBoostTimerRef.current) {
        clearTimeout(galaxyBoostTimerRef.current);
      }
    };
  }, []);

  // Animaciones para roadmap, equipo, etc.
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Datos del equipo
  const teamMembers = [
    {
      name: "Snayder Rodríguez",
      image: TeamSnayder,
      role: t("aboutUs.roles.backend"),
      description: t("aboutUs.descriptions.snayder"),
      socialLinks: [
        {
          platform: "linkedin",
          link: "https://www.linkedin.com/in/snayder-rodriguez-24a8bb18a/",
        },
        {
          platform: "envelope",
          link: "https://mail.google.com/mail/?view=cm&to=jakub@zenitailab.com",
        },
      ],
    },
    {
      name: "Yostian Campos",
      image: TeamYostian,
      role: t("aboutUs.roles.frontend"),
      description: t("aboutUs.descriptions.yostian"),
      socialLinks: [
        {
          platform: "linkedin",
          link: "https://www.linkedin.com/in/yostian-campos-0490a8321/",
        },
        {
          platform: "envelope",
          link: "https://mail.google.com/mail/?view=cm&to=yostian@zenitailab.com",
        },
      ],
    },
    {
      name: "Marlon Marin",
      image: TeamMarlon,
      role: t("aboutUs.roles.projectManager"),
      description: t("aboutUs.descriptions.marlon"),
      socialLinks: [
        {
          platform: "linkedin",
          link: "https://www.linkedin.com/in/marlon-marin-productividad-ba571a2b/",
        },
        {
          platform: "envelope",
          link: "https://mail.google.com/mail/?view=cm&to=marlon@zenitailab.com",
        },
      ],
    },
    {
      name: "David Mora",
      image: TeamDavid,
      role: t("aboutUs.roles.aiEngineer"),
      description: t("aboutUs.descriptions.david"),
      socialLinks: [
        {
          platform: "linkedin",
          link: "https://www.linkedin.com/in/ddmoracr/",
        },
        {
          platform: "envelope",
          link: "https://mail.google.com/mail/?view=cm&to=david@zenitailab.com",
        },
      ],
    },
    {
      name: "Marlon Molina",
      image: TeamMarlonM,
      role: t("aboutUs.roles.machineLearning"),
      description: t("aboutUs.descriptions.marlonM"),
      socialLinks: [
        {
          platform: "linkedin",
          link: "https://www.linkedin.com/in/marlon-marin-productividad-ba571a2b/",
        },
        {
          platform: "envelope",
          link: "https://mail.google.com/mail/?view=cm&to=marlon@zenitailab.com",
        },
      ],
    },
  ];

  const handleCardClick = (index) => {
    setSelectedCard(selectedCard === index ? null : index);
  };

  return (
    <div className="about-us-container">
      <Header />

      {/* Sección Misión / Visión - ocupa toda la pantalla */}
      <section className="mission-vision-section">
        <div className="mission-vision-container">
          <div className="mission-vision-card">
            <h2>{t("aboutUs.mission.title")}</h2>
            <p>{t("aboutUs.mission.text")}</p>
          </div>
          <div className="mission-vision-card">
            <h2>{t("aboutUs.vision.title")}</h2>
            <p>{t("aboutUs.vision.text")}</p>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <motion.div
        className={`roadmap-section roadmap-section--${activeQuarterKey ? activeQuarterKey.toLowerCase() : "idle"}`}
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
      >
        <div className="roadmap-galaxy-bg" aria-hidden="true">
          <Galaxy
            mouseRepulsion
            mouseInteraction={false}
            density={1.8}
            glowIntensity={galaxyGlow}
            saturation={0.18}
            hueShift={272}
            twinkleIntensity={0.2}
            rotationSpeed={0.1}
            repulsionStrength={2}
            autoCenterRepulsion={0}
            starSpeed={0.5}
            speed={galaxySpeed}
            transparent={false}
          />
        </div>

        <motion.h1
          className="roadmap-title"
          variants={fadeIn}
          transition={{ duration: 0.8 }}
        >
          {t("aboutUs.roadmap.title")}
        </motion.h1>

        <motion.p
          className="roadmap-description"
          variants={fadeIn}
          transition={{ duration: 0.8 }}
        >
          <span className="roadmap-highlight">
            {t("aboutUs.roadmap.pressTheCircleHighlight")}
          </span>{" "}
          {t("aboutUs.roadmap.pressTheCircleRest")}
        </motion.p>

        <motion.div
          className="roadmap-timeline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
        >
          {quarterKeys.map((quarterKey, index) => {
            const quarter = t(`aboutUs.roadmap.quarters.${quarterKey}`);
            return (
              <motion.div
                className={`roadmap-item ${index % 2 === 0 ? "roadmap-item--top" : "roadmap-item--bottom"} ${activeQuarterKey === quarterKey ? "is-active" : ""}`}
                key={quarterKey}
                variants={fadeIn}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.35 }}
                onClick={() => handleQuarterSelect(quarterKey)}
              >
                <span className="roadmap-point" aria-hidden="true">
                  <i className="roadmap-point-core" />
                </span>
                <div className="roadmap-event-card">
                  <div className="roadmap-event-meta">
                    <span className="roadmap-label">{quarter}</span>
                    <span className="roadmap-year">{t("aboutUs.roadmap.year")}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <div className={`roadmap-focus-stage ${activeQuarterKey ? "has-content" : ""}`}>
          <AnimatePresence mode="wait" initial={false}>
            {activeQuarterKey ? (
              <motion.div
                className="roadmap-focus-panel"
                key={activeQuarterKey}
                initial={{ opacity: 0, y: 20, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.99 }}
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              >
                <header className="roadmap-focus-header">
                  <p>{t("aboutUs.roadmap.title")}</p>
                  <h3>
                    {activeQuarterLabel} {t("aboutUs.roadmap.year")}
                  </h3>
                </header>
                <ul className="roadmap-focus-list">
                  {activeQuarterContent.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Meet the Team */}
      <motion.div
        className="meet-the-team-section"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
      >
        <h1 className="team-title">{t("aboutUs.meetTheTeam")}</h1>
        <div className="team-container">
          {teamMembers.map((member, index) => (
            <ProfileCard
              key={index}
              name={member.name}
              image={member.image}
              role={member.role}
              description={member.description}
              socialLinks={member.socialLinks}
              isSelected={selectedCard === index}
              onClick={() => handleCardClick(index)}
            />
          ))}
        </div>
      </motion.div>

    </div>
  );
}
